import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { formatINR, formatDate, formatPct, cplHealth, statusColor } from "../utils/format";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Pause, Play, Wallet, Star, ArrowLeft, Sparkles, History } from "lucide-react";

export default function CampaignDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [ai, setAi] = useState(null);
  const [budgetEdit, setBudgetEdit] = useState(false);
  const [newBudget, setNewBudget] = useState("");

  const load = () => api.get(`/campaigns/${id}`).then((r) => { setC(r.data); setNewBudget(r.data.daily_budget); });

  useEffect(() => {
    load();
    api.get(`/ai/campaign/${id}`).then((r) => setAi(r.data));
  }, [id]);

  const action = async (act, extra = {}) => {
    try {
      await api.post(`/campaigns/${id}/action`, { action: act, ...extra });
      toast.success(`Campaign ${act.replace("_", " ")} — applied`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  if (!c) return <Layout title="Loading…"><div className="text-sm text-muted-foreground">Loading campaign…</div></Layout>;

  return (
    <Layout title={c.name} subtitle={c.client_name}>
      <Link to="/campaigns" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to campaigns
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column - metadata + actions */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-2">Status</div>
            <div className="flex items-center gap-2 mb-4">
              <StatusBadge tone={statusColor(c.status)}>{c.status}</StatusBadge>
              {c.watchlist && <StatusBadge tone="warning"><Star className="w-3 h-3 inline mr-1" /> Watchlist</StatusBadge>}
            </div>

            <dl className="text-sm space-y-2.5">
              <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd>{c.type}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Start</dt><dd>{formatDate(c.start_date)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">End</dt><dd>{formatDate(c.end_date)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Target CPL</dt><dd>{formatINR(c.target_cpl)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Daily Budget</dt><dd className="font-medium">{formatINR(c.daily_budget)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Monthly Budget</dt><dd>{formatINR(c.monthly_budget)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">0-Lead Streak</dt>
                <dd className={c.zero_lead_streak > 2 ? "text-[hsl(var(--critical))] font-semibold" : ""}>{c.zero_lead_streak} day(s)</dd>
              </div>
            </dl>
          </div>

          <div className="bg-card border border-border rounded-sm p-5" data-testid="campaign-actions">
            <div className="label-mono mb-3">Actions</div>
            <div className="grid grid-cols-2 gap-2">
              {c.status !== "paused" ? (
                <button
                  onClick={() => window.confirm(`Pause "${c.name}"?`) && action("pause")}
                  data-testid="pause-campaign-btn"
                  className="h-9 flex items-center justify-center gap-1.5 text-xs font-medium border border-[hsl(var(--warning))]/40 text-[hsl(38_92%_30%)] hover:bg-[hsl(var(--warning))]/10 rounded-sm"
                >
                  <Pause className="w-3.5 h-3.5" /> Pause
                </button>
              ) : (
                <button
                  onClick={() => action("enable")}
                  data-testid="enable-campaign-btn"
                  className="h-9 flex items-center justify-center gap-1.5 text-xs font-medium border border-[hsl(var(--success))]/40 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10 rounded-sm"
                >
                  <Play className="w-3.5 h-3.5" /> Enable
                </button>
              )}
              <button
                onClick={() => setBudgetEdit(true)}
                data-testid="budget-edit-btn"
                className="h-9 flex items-center justify-center gap-1.5 text-xs font-medium border border-border hover:bg-secondary rounded-sm"
              >
                <Wallet className="w-3.5 h-3.5" /> Budget
              </button>
              <button
                onClick={() => action("watchlist")}
                data-testid="watchlist-toggle-btn"
                className={`col-span-2 h-9 flex items-center justify-center gap-1.5 text-xs font-medium border rounded-sm ${
                  c.watchlist ? "border-[hsl(var(--warning))]/40 text-[hsl(38_92%_30%)]" : "border-border hover:bg-secondary"
                }`}
              >
                <Star className="w-3.5 h-3.5" /> {c.watchlist ? "Remove from Watchlist" : "Add to Watchlist"}
              </button>
            </div>

            {budgetEdit && (
              <div className="mt-4 pt-4 border-t border-border" data-testid="budget-edit-form">
                <label className="label-mono block mb-1.5">New Daily Budget (₹)</label>
                <input
                  type="number" value={newBudget}
                  onChange={(e) => setNewBudget(e.target.value)}
                  className="w-full h-9 px-3 border border-border rounded-sm text-sm"
                  data-testid="new-budget-input"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={async () => { await action("update_budget", { new_budget: parseFloat(newBudget) }); setBudgetEdit(false); }}
                    data-testid="budget-save-btn"
                    className="flex-1 h-9 bg-primary text-primary-foreground text-xs font-medium rounded-sm hover:bg-primary/90"
                  >
                    Apply Change
                  </button>
                  <button onClick={() => setBudgetEdit(false)} className="flex-1 h-9 border border-border text-xs rounded-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {c.landing_page && (
            <div className="bg-card border border-border rounded-sm p-5">
              <div className="label-mono mb-2">Landing Page</div>
              <div className="font-medium text-sm mb-1">{c.landing_page.title}</div>
              <div className="text-xs text-muted-foreground mb-3 truncate">{c.landing_page.url}</div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Health Score</span>
                <span className="font-semibold tabular">{c.landing_page.health_score}/100</span>
              </div>
            </div>
          )}
        </div>

        {/* Right - charts + AI + history */}
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-sm p-4 kpi-border-primary">
              <div className="label-mono">Spend · 30d</div>
              <div className="font-heading text-2xl font-light mt-2 tabular">{formatINR(c.spend_30d, { compact: true })}</div>
            </div>
            <div className="bg-card border border-border rounded-sm p-4 kpi-border-success">
              <div className="label-mono">Leads · 30d</div>
              <div className="font-heading text-2xl font-light mt-2 tabular">{c.leads_30d}</div>
            </div>
            <div className={`bg-card border border-border rounded-sm p-4 kpi-border-${cplHealth(c.cpl_30d)}`}>
              <div className="label-mono">CPL · 30d</div>
              <div className="font-heading text-2xl font-light mt-2 tabular">{c.cpl_30d ? formatINR(c.cpl_30d) : "—"}</div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-1">Trend</div>
            <div className="font-heading text-lg mb-3 tracking-tight">Daily Spend · Leads · CPL</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={c.daily}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(220 15% 90%)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid hsl(220 15% 90%)", fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="spend" stroke="hsl(214 100% 35%)" strokeWidth={2} dot={false} name="Spend ₹" />
                <Line yAxisId="right" type="monotone" dataKey="leads" stroke="hsl(150 70% 30%)" strokeWidth={2} dot={false} name="Leads" />
                <Line yAxisId="left" type="monotone" dataKey="cpl" stroke="hsl(3 75% 45%)" strokeWidth={1.5} dot={false} strokeDasharray="4 4" name="CPL ₹" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {ai && (
            <div className="bg-card border border-border border-t-2 border-t-[hsl(var(--ai))] rounded-sm p-5" data-testid="ai-campaign-insight">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" />
                <div className="label-mono text-[hsl(var(--ai))]">AI Analysis</div>
                <StatusBadge tone={ai.urgency === "high" ? "critical" : ai.urgency === "moderate" ? "warning" : "success"}>{ai.urgency} urgency</StatusBadge>
              </div>
              <div className="text-sm text-muted-foreground mb-3">{ai.context}</div>
              <div className="space-y-2">
                {ai.findings.map((f, i) => <div key={i} className="text-sm"><span className="label-mono mr-2">Finding</span>{f}</div>)}
                {ai.recommendations.map((r, i) => <div key={i} className="text-sm"><span className="label-mono mr-2">Action</span><span className="font-medium">{r}</span></div>)}
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-muted-foreground" />
              <div className="label-mono">Change Log</div>
            </div>
            <div className="space-y-1">
              {(c.change_log || []).slice(0, 10).map((l) => (
                <div key={l.id} className="flex items-center justify-between text-xs py-2 border-b border-border last:border-b-0">
                  <span className="font-medium">{l.action}</span>
                  <span className="text-muted-foreground">{l.user_name}</span>
                  <span className="text-muted-foreground">{formatDate(l.created_at)}</span>
                </div>
              ))}
              {(!c.change_log || c.change_log.length === 0) && <div className="text-xs text-muted-foreground py-2">No changes yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
